// AdminUsersPage.jsx
import React, { useState, useEffect } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';

const AdminUsersPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userType, setUserType] = useState('Ship'); // Default user type
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [department, setDepartment] = useState('Technical'); // Default department
  const [role, setRole] = useState('');
  const [company, setCompany] = useState('');
  const [shipsManaged, setShipsManaged] = useState([]);

  const [companies, setCompanies] = useState([]);
  const [companyShips, setCompanyShips] = useState([]);
  const [sectionOpen, setSectionOpen] = useState(true);

  // Fetch companies from the API (with their ships populated)
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/companies`)
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => setCompanies(data))
      .catch(error => console.error('Error fetching companies:', error));
  }, []);

  // Toggle the User Details section
  const toggleSection = () => {
    setSectionOpen(prev => !prev);
  };

  // Update company selection and set available ships for that company
  const handleCompanyChange = (e) => {
    const selectedCompanyId = e.target.value;
    setCompany(selectedCompanyId);
    // Reset ships managed when company changes
    setShipsManaged([]);
    // Find the selected company from the companies list
    const selectedCompany = companies.find(comp => comp._id === selectedCompanyId);
    if (selectedCompany && selectedCompany.ships) {
      setCompanyShips(selectedCompany.ships);
    } else {
      setCompanyShips([]);
    }
  };

  // Handle selection of ships managed
  const handleShipsManagedChange = (e) => {
    const options = e.target.options;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selected.push(options[i].value);
      }
    }
    setShipsManaged(selected);
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    const userData = {
      firstName,
      lastName,
      userType,
      email,
      password,
      phoneNumber,
      department,
      role,
      company,
      shipsManaged, // Array of ship IDs (from the selected company)
    };

    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData)
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        return response.json();
      })
      .then(data => {
        console.log('User saved successfully:', data);
        // Reset the form fields after successful submission
        setFirstName('');
        setLastName('');
        setUserType('Ship');
        setEmail('');
        setPassword('');
        setPhoneNumber('');
        setDepartment('Technical');
        setRole('');
        setCompany('');
        setShipsManaged([]);
        setCompanyShips([]);
      })
      .catch(error => console.error('Error saving user:', error));
  };

  return (
    <div className="container-fluid px-4 py-3">
      <h2 className="mb-4">Admin - Users</h2>
      <form onSubmit={handleSubmit}>
        {/* User Details Section */}
        <div className="mb-4">
          <div className="d-flex justify-content-between align-items-center bg-light p-2 border">
            <h4 className="mb-0">User Details</h4>
            <button type="button" className="btn btn-link" onClick={toggleSection}>
              {sectionOpen ? '▼' : '►'}
            </button>
          </div>
          {sectionOpen && (
            <div className="p-3 border border-top-0">
              {/* Row 1: First Name & Last Name */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="firstName" className="form-label">First Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="firstName"
                    name="firstName"
                    placeholder="Enter first name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="lastName" className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="lastName"
                    name="lastName"
                    placeholder="Enter last name"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    required
                  />
                </div>
              </div>
              {/* Row 2: User Type & Email */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="userType" className="form-label">User Type</label>
                  <select
                    className="form-select"
                    id="userType"
                    name="userType"
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    required
                  >
                    <option value="Ship">Ship</option>
                    <option value="Company Admin">Company Admin</option>
                    <option value="Super Admin">Super Admin</option>
                    <option value="Admin">Admin</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label htmlFor="email" className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    name="email"
                    placeholder="Enter email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>
              </div>
              {/* Row 3: Password & Phone Number */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="password" className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-control"
                    id="password"
                    name="password"
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="phoneNumber" className="form-label">Phone Number</label>
                  <input
                    type="text"
                    className="form-control"
                    id="phoneNumber"
                    name="phoneNumber"
                    placeholder="Enter phone number"
                    value={phoneNumber}
                    onChange={(e) => setPhoneNumber(e.target.value)}
                  />
                </div>
              </div>
              {/* Row 4: Department & Role */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="department" className="form-label">Department</label>
                  <select
                    className="form-select"
                    id="department"
                    name="department"
                    value={department}
                    onChange={(e) => setDepartment(e.target.value)}
                    required
                  >
                    <option value="Technical">Technical</option>
                    <option value="Operations">Operations</option>
                    <option value="Performance">Performance</option>
                    <option value="Chartering">Chartering</option>
                    <option value="Management">Management</option>
                  </select>
                </div>
                <div className="col-md-6">
                  <label htmlFor="role" className="form-label">Role</label>
                  <input
                    type="text"
                    className="form-control"
                    id="role"
                    name="role"
                    placeholder="Enter role"
                    value={role}
                    onChange={(e) => setRole(e.target.value)}
                  />
                </div>
              </div>
              {/* Row 5: Company & Ships Managed */}
              <div className="row mb-3">
                <div className="col-md-6">
                  <label htmlFor="company" className="form-label">Company</label>
                  <select
                    className="form-select"
                    id="company"
                    name="company"
                    value={company}
                    onChange={handleCompanyChange}
                    required
                  >
                    <option value="">Select a company</option>
                    {companies.map((comp) => (
                      <option key={comp._id} value={comp._id}>
                        {comp.companyName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="col-md-6">
                  <label htmlFor="shipsManaged" className="form-label">Ships Managed</label>
                  <select
                    multiple
                    className="form-select"
                    id="shipsManaged"
                    name="shipsManaged"
                    value={shipsManaged}
                    onChange={handleShipsManagedChange}
                    size="10"
                    disabled={!company}
                  >
                    {companyShips.map((ship) => (
                      <option key={ship._id} value={ship._id}>
                        {ship.vesselName}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>
        <button type="submit" className="btn btn-primary">Save User Details</button>
      </form>
    </div>
  );
};

export default AdminUsersPage;
